/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import React, {useEffect, useState, useRef} from 'react'
import * as Yup from 'yup'
import {Formik} from 'formik'
import useUtils from './useUtils'
import {useLocation, useNavigate} from 'react-router-dom'
import {FaTimes} from 'react-icons/fa'
import {useDispatch} from 'react-redux'
import {setTitle} from '../../redux/reducers/mainSlice'
import AssignConfirmation from '../../modules/alert/AssignConfirmation'
import CustomFilterDropdown from '../../modules/common/CustomFilterDropdown'

const AddVideo = () => {
  const dispatch = useDispatch()
  dispatch(setTitle('Video'))
  const [isOpen, setIsOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [transcriptFile, setTranscriptFile] = useState(null)
  const [transcriptData, setTranscriptData] = useState(null)
  
  const handleInputChange = (e) => {
    setInputValue(e.target.value)
  }

  const handleTranscriptFileChange = (e) => {
    const file = e.target.files[0]
    if (file && file.type === 'application/json') {
      setTranscriptFile(file)
      const reader = new FileReader()
      reader.onload = (event) => {
        try {
          const jsonData = JSON.parse(event.target.result)
          
          // Parse cue_start and cue_end from string to number
          const processedData = processTranscriptData(jsonData)
          setTranscriptData(processedData)
        } catch (error) {
          console.error('Error parsing JSON file:', error)
          setTranscriptData(null)
          alert('Invalid JSON file. Please select a valid JSON file.')
        }
      }
      reader.readAsText(file)
    }
  }

  const processTranscriptData = (data) => {
    // If data is an array of cues
    if (Array.isArray(data)) {
      return data.map(cue => ({
        ...cue,
        cue_start: typeof cue.cue_start === 'string' ? parseFloat(cue.cue_start) : cue.cue_start,
        cue_end: typeof cue.cue_end === 'string' ? parseFloat(cue.cue_end) : cue.cue_end
      }))
    }
    
    // If data is an object with cues array
    if (data.cues && Array.isArray(data.cues)) {
      return {
        ...data,
        cues: data.cues.map(cue => ({
          ...cue,
          cue_start: typeof cue.cue_start === 'string' ? parseFloat(cue.cue_start) : cue.cue_start,
          cue_end: typeof cue.cue_end === 'string' ? parseFloat(cue.cue_end) : cue.cue_end
        }))
      }
    }
    
    // If data is a single cue object
    if (data.cue_start !== undefined || data.cue_end !== undefined) {
      return {
        ...data,
        cue_start: typeof data.cue_start === 'string' ? parseFloat(data.cue_start) : data.cue_start,
        cue_end: typeof data.cue_end === 'string' ? parseFloat(data.cue_end) : data.cue_end
      }
    }
    
    // Return data as is if no cue_start/cue_end found
    return data
  }

  function handleAddtags(values, setFieldValue) {
    if (inputValue !== '') {
      setFieldValue('tags', [...values.tags, inputValue])
      setInputValue('')
    }
  }

  const location = useLocation()
  const locationState = location?.state?.values || null
  const navigate = useNavigate()

  useEffect(() => {
    if (locationState) {
      console.log(locationState)
      console.log('isVideoTranscriptExist', locationState.isVideoTranscriptExist)
    }
  }, [locationState])

  const [languageOptions, setLanguageOptions] = useState([])
  const [programOptions, setProgramOptions] = useState([])
  const [courseOptions, setCourseOptions] = useState([])
  const [moduleOptions, setModuleOptions] = useState([])
  const [unitOptions, setUnitOptions] = useState([])

  // Token-based pagination states
  const [programNextPageToken, setProgramNextPageToken] = useState(null)
  const [courseNextPageToken, setCourseNextPageToken] = useState(null)
  const [moduleNextPageToken, setModuleNextPageToken] = useState(null)
  const [unitNextPageToken, setUnitNextPageToken] = useState(null)
  
  // Loading states
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(false)
  const [isLoadingCourses, setIsLoadingCourses] = useState(false)
  const [isLoadingModules, setIsLoadingModules] = useState(false)
  const [isLoadingUnits, setIsLoadingUnits] = useState(false)

  const {
    fetchLanguages,
    fetchProgram,
    fetchCourse,
    fetchModule,
    fetchUnit,
    submitVideo,
    modifyVideo,
  } = useUtils()

  // Loading functions for token-based pagination
  const loadMorePrograms = async () => {
    if (isLoadingPrograms || !programNextPageToken) return
    
    setIsLoadingPrograms(true)
    try {
      const response = await fetchProgram(programNextPageToken)
      if (response) {
        setProgramOptions(prev => [...prev, ...response.records])
        setProgramNextPageToken(response.nextPageToken)
      }
    } catch (error) {
      console.error('Error loading more programs:', error)
    } finally {
      setIsLoadingPrograms(false)
    }
  }

  const loadMoreCourses = async (programId) => {
    if (isLoadingCourses || !courseNextPageToken || !programId) return
    
    setIsLoadingCourses(true)
    try {
      const response = await fetchCourse(programId, courseNextPageToken)
      if (response) {
        setCourseOptions(prev => [...prev, ...response.records])
        setCourseNextPageToken(response.nextPageToken)
      }
    } catch (error) {
      console.error('Error loading more courses:', error)
    } finally {
      setIsLoadingCourses(false)
    }
  }

  const loadMoreModules = async (courseId) => {
    if (isLoadingModules || !moduleNextPageToken || !courseId) return
    
    setIsLoadingModules(true)
    try {
      const response = await fetchModule(courseId, moduleNextPageToken)
      if (response) {
        setModuleOptions(prev => [...prev, ...response.records])
        setModuleNextPageToken(response.nextPageToken)
      }
    } catch (error) {
      console.error('Error loading more modules:', error)
    } finally {
      setIsLoadingModules(false)
    }
  }

  const loadMoreUnits = async (moduleId) => {
    if (isLoadingUnits || !unitNextPageToken || !moduleId) return
    
    setIsLoadingUnits(true)
    try {
      const response = await fetchUnit(moduleId, unitNextPageToken)
      if (response) {
        setUnitOptions(prev => [...prev, ...response.records])
        setUnitNextPageToken(response.nextPageToken)
      }
    } catch (error) {
      console.error('Error loading more units:', error)
    } finally {
      setIsLoadingUnits(false)
    }
  }

  const initialValues = locationState
    ? {
        id: locationState?.id,
        isEdit: true,
        title: locationState?.title,
        link: locationState?.link,
        language: locationState?.language?.name ?? '',
        languageId: locationState?.language?.id ?? '',
        programId: programOptions[0]?.id ?? '',
        courseId: courseOptions[0]?.id ?? '',
        moduleId: moduleOptions[0]?.id ?? '',
        unitId: locationState?.unitVideo[0]?.unit?.id ?? '',
        unitName: locationState?.unitVideo[0]?.unit?.name ?? '',
        description: locationState?.description,
        tags: locationState?.tag.map((t) => t.name),
        isVideoTranscriptExist: locationState?.isVideoTranscriptExist ?? false
      }
    : {
        title: '',
        description: '',
        languageName: '',
        link: '',
        language: languageOptions[0]?.name ?? '',
        languageId: languageOptions[0]?.id ?? '',
        programId: programOptions[0]?.id ?? '',
        courseId: courseOptions[0]?.id ?? '',
        moduleId: moduleOptions[0]?.id ?? '',
        unitId: unitOptions[0]?.id ?? '',
        tags: [],
        description: '',
        isVideoTranscriptExist: false
      }

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const languages = await fetchLanguages()
        setLanguageOptions(languages)
        
        const programs = await fetchProgram()
        if (programs) {
          setProgramOptions(programs.records)
          setProgramNextPageToken(programs.nextPageToken)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    }
    fetchInitialData()
  }, [])

  const handleProgramChange = async (programId, setFieldValue) => {
    setFieldValue('programId', programId)
    setFieldValue('courseId', '')
    setFieldValue('moduleId', '')
    setFieldValue('unitId', '')

    // Reset downstream filters
    setCourseOptions([])
    setCourseNextPageToken(null)
    setModuleOptions([])
    setModuleNextPageToken(null)
    setUnitOptions([])
    setUnitNextPageToken(null)

    if (programId) {
      setIsLoadingCourses(true)
      try {
        const courses = await fetchCourse(programId)
        if (courses) {
          setCourseOptions(courses.records)
          setCourseNextPageToken(courses.nextPageToken)
        }
      } catch (error) {
        console.error('Error loading courses:', error)
      } finally {
        setIsLoadingCourses(false)
      }
    }
  }

  const handleCourseChange = async (courseId, setFieldValue) => {
    setFieldValue('courseId', courseId)
    setFieldValue('moduleId', '')
    setFieldValue('unitId', '')

    // Reset downstream filters
    setModuleOptions([])
    setModuleNextPageToken(null)
    setUnitOptions([])
    setUnitNextPageToken(null)

    if (courseId) {
      setIsLoadingModules(true)
      try {
        const modules = await fetchModule(courseId)
        if (modules) {
          setModuleOptions(modules.records)
          setModuleNextPageToken(modules.nextPageToken)
        }
      } catch (error) {
        console.error('Error loading modules:', error)
      } finally {
        setIsLoadingModules(false)
      }
    }
  }

  const handleModuleChange = async (moduleId, setFieldValue) => {
    setFieldValue('moduleId', moduleId)
    setFieldValue('unitId', '')

    // Reset downstream filters
    setUnitOptions([])
    setUnitNextPageToken(null)

    if (moduleId) {
      setIsLoadingUnits(true)
      try {
        const units = await fetchUnit(moduleId)
        if (units) {
          setUnitOptions(units.records)
          setUnitNextPageToken(units.nextPageToken)
        }
      } catch (error) {
        console.error('Error loading units:', error)
      } finally {
        setIsLoadingUnits(false)
      }
    }
  }

  const validationSchema = Yup.object().shape({
    title: Yup.string().required('Please enter program title'),
    link: Yup.string().required('Please enter video link'),
    languageId: Yup.string().required('Please select language'),
    unitId: Yup.string().required('Please select unit'),
    description: Yup.string().required('Please enter short description'),
    tags: Yup.array().min(1, 'Tags are mandatory').required('Tags are mandatory'),
  })

  const handleSubmit = (values) => {
    const data = {
      title: values.title,
      link: values.link,
      description: values.description,
      languageId: values.languageId,
      unitId: values.unitId,
      tags: values.tags,
    }

    // Include transcript data if available
    if (transcriptData) {
      data.transcript = transcriptData
    }

    if (values.isEdit) {
      modifyVideo(values.id, data)
    } else {
      submitVideo(data)
    }
  }

  return (
    <>
      <div className='card card-custom'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({values, touched, handleChange, errors, handleBlur, handleSubmit, setFieldValue}) => (
            <form className='form' onSubmit={handleSubmit}>
              <div className='card-body'>
                <div className='tab-content pt-3'>
                  <div className='row align-items-start flex-wrap'>
                    {/* Title Field */}
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Title</label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        placeholder='Title'
                        name='title'
                        value={values.title}
                        onChange={handleChange}
                      />
                      {touched.title && errors.title && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.title}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Link Field */}
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Link</label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                        placeholder='Video link'
                        name='link'
                        value={values.link}
                        onChange={handleChange}
                      />
                      {touched.link && errors.link && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.link}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Language Select */}
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Language</label>
                      <select
                        className='form-select form-select-solid mb-3 mb-lg-0'
                        name='languageId'
                        value={values.languageId} // Set the value to languageId from the form state
                        onChange={(e) => setFieldValue('languageId', e.target.value)} // Update languageId on selection
                      >
                        <option value=''>Select Language</option>
                        {languageOptions?.map((language, index) => (
                          <option key={index} value={language.id}>
                            {language.name}
                          </option>
                        ))}
                      </select>
                      {touched.languageId && errors.languageId && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.languageId}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Program, Course, Module, and Unit Selection */}
                  <div className='row align-items-center flex-wrap mt-5'>
                    {/* Program Select */}
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Program</label>
                      <CustomFilterDropdown
                        options={programOptions}
                        selectedValue={values.programId}
                        placeholder="Select Program"
                        className="form-select  form-select-solid mb-3 mb-lg-0"
                        displayField="title"
                        formik={true}
                        setFormikField={(value) => handleProgramChange(value, setFieldValue)}
                        onScrollEnd={loadMorePrograms}
                        isLoading={isLoadingPrograms}
                      />
                    </div>

                    {/* Course Select */}
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Course</label>
                      <CustomFilterDropdown
                        options={courseOptions}
                        selectedValue={values.courseId}
                        placeholder="Select Course"
                        className="form-select  form-select-solid mb-3 mb-lg-0"
                        displayField="title"
                        formik={true}
                        setFormikField={(value) => handleCourseChange(value, setFieldValue)}
                        onScrollEnd={() => loadMoreCourses(values.programId)}
                        isLoading={isLoadingCourses}
                      />
                    </div>

                    {/* Module Select */}
                    <div className='col'>
                      <label className='col-form-label required fw-bold fs-6'>Module</label>
                      <CustomFilterDropdown
                        options={moduleOptions}
                        selectedValue={values.moduleId}
                        placeholder="Select Module"
                        className="form-select  form-select-solid mb-3 mb-lg-0"
                        displayField="title"
                        formik={true}
                        setFormikField={(value) => handleModuleChange(value, setFieldValue)}
                        onScrollEnd={() => loadMoreModules(values.courseId)}
                        isLoading={isLoadingModules}
                      />
                    </div>
                  </div>

                  <div className='row mt-7'>
                    <div className='col-4'>
                      <label className='col-form-label required fw-bold fs-6'>Unit</label>
                      {/* Check if moduleId is present */}
                        <CustomFilterDropdown
                          options={unitOptions}
                          selectedValue={values.unitId}
                          placeholder="Select Unit"
                          className="form-select  form-select-solid mb-3 mb-lg-0"
                          displayField="name"
                          formik={true}
                          setFormikField={(value) => setFieldValue('unitId', value)}
                          onScrollEnd={() => loadMoreUnits(values.moduleId)}
                          isLoading={isLoadingUnits}
                        />
                    </div>
                    {touched.unitId && errors.unitId && (
                      <div className='fv-plugins-message-container'>
                        <span role='alert' className='text-danger'>
                          {errors.unitId}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className='row mt-7'>
                    <div className='col-8'>
                      <label className='col-form-label fw-bold fs-6'>Transcript File</label>
                      <div className='d-flex align-items-center gap-3'>
                        <input
                          type='file'
                          accept='.json'
                          onChange={handleTranscriptFileChange}
                          className='form-control form-control-solid'
                          style={{ width: 'auto' }}
                        />
                        <button
                          type='button'
                          className='btn btn-primary'
                          onClick={() => {
                            const fileInput = document.querySelector('input[type="file"]')
                            if (fileInput) fileInput.click()
                          }}
                        >
                          Add Transcript
                        </button>
                      </div>
                    </div>
                  </div>

                     {values.isEdit&&<div className='col-4 mt-7'>
                    <div className='d-flex align-items-center gap-5'>
                    <span className='col-form-label fw-bold fs-6'>Transcript</span>
                    <div>
                      <label className='form-check form-check-custom form-check-solid form-switch '>
                        <input
                          style={{height: '18px', width: '18px'}}
                          className='form-check-input-sm'
                          type='checkbox'
                          name='isVideoTranscriptExist'
                          checked={values.isVideoTranscriptExist}
                        />
                      </label>
                    </div>
                    </div>
                  </div>}

                  <div className=' mt-7 row'>
                    <div className='col-4'>
                      <label className='col-form-label required fw-bold fs-6'>Tags</label>
                      <input
                        type='text'
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 '
                        placeholder='Add tags'
                        name='tags'
                        value={inputValue}
                        onChange={(e) => handleInputChange(e)}
                      />
                      <button
                        type='button'
                        className='btn btn-primary mt-4 fs-7'
                        onClick={() => handleAddtags(values, setFieldValue)}
                      >
                        <span className='indicator-label'>Add</span>
                      </button>
                    </div>
                  </div>
                  {touched.tags && errors.tags && (
                    <div className='fv-plugins-message-container'>
                      <span role='alert' className='text-danger'>
                        {errors.tags}
                      </span>
                    </div>
                  )}

                  <div className='d-flex align-items-center flex-wrap gap-5 mt-7 '>
                    {values?.tags?.map((val, index) => (
                      <div
                        key={index}
                        className='d-flex gap-8 align-items-center bg-light-primary px-8 py-3 rounded-4 '
                        style={{cursor: 'pointer'}}
                      >
                        <a href='#' className='text-primary fw-semibold fs-6 me-3'>
                          {val}{' '}
                        </a>
                        <div className='rounded-3 p-1' style={{background: 'white'}}>
                          <FaTimes
                            style={{
                              color: 'black',
                              cursor: 'pointer',
                              fontSize: '18px',
                            }}
                            onClick={() =>
                              setFieldValue(
                                'tags',
                                values.tags.filter((_, i) => i !== index)
                              )
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>

               

                  <div className='row mt-7'>
                    <div className='col-8'>
                      <label className='col-form-label required fw-bold fs-6'>
                        Short Description
                      </label>
                      <textarea
                        className='form-control form-control-lg form-control-solid mb-3 mb-lg-0 '
                        style={{height: '200px', width: '100%'}}
                        placeholder='Short description'
                        name='description'
                        value={values.description}
                        onChange={(e) => {
                          if (e.target.value.length <= 150) {
                            handleChange(e)
                          }
                        }}
                      />
                      <small className='text-muted'>
                        {values.description.length}/150 characters
                      </small>
                      {touched.description && errors.description && (
                        <div className='fv-plugins-message-container'>
                          <span role='alert' className='text-danger'>
                            {errors.description}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className='d-flex align-items-center justify-content-end my-5'>
                <button
                  type='button'
                  className='btn btn-danger ms-2 mt-2 me-2'
                  onClick={() => navigate('/videolist')}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  onClick={() => {
                    if (values?.isEdit) {
                      setIsOpen(true) // Open the confirmation modal
                    } else {
                      document.querySelector('form')?.requestSubmit() // Directly submit in create mode
                    }
                  }}
                  className='btn btn-primary ms-2 mt-2 me-2'
                >
                  {!values.isEdit ? 'Submit' : 'Update'}
                </button>
              </div>
            </form>
          )}
        </Formik>
      </div>
      <AssignConfirmation
        isVisible={isOpen}
        message='Are you sure you want to update this video?'
        onConfirm={() => {
          setIsOpen(false)
          setTimeout(() => {
            document.querySelector('form')?.requestSubmit() // Triggers form submission
          }, 0)
        }}
        onCancel={() => setIsOpen(false)}
      />
    </>
  )
}

export default AddVideo
